/**
 * Every game in one list, solo and online together.
 *
 * The scoring half of each entry comes from `@arena/games`, which the server
 * uses too, so a game cannot be ranked one way here and another way there. This
 * file adds only what the browser needs: the icon, the colour, the wording, and
 * for solo games the function that builds the thing.
 *
 * Online games have no `mount`. They are not played on this page at all: the
 * hub hands off to the arena, which is a separate document because it loads a
 * renderer and a simulation that a game of Snake has no business paying for.
 */
import { GAMES as META, gameById, type GameKind } from '@arena/games';

import { aimTrainer, clickSpeed, colorMatch, reactionTime } from './games/reflex.js';
import { flappy, pong, snake } from './games/classics.js';
import { g2048, memory, minesweeper, simonSays } from './games/puzzle.js';
import { anagram, hangman, speedTyping, wordle } from './games/words.js';
import { blackjack, rockPaperScissors, ticTacToe } from './games/strategy.js';
import { mathSprint, quiz } from './games/mind.js';
import type { Category, Difficulty, GameDef } from './types.js';

export interface CatalogueEntry {
  id: string;
  title: string;
  kind: GameKind;
  blurb: string;
  category: Category;
  difficulty: Difficulty;
  icon: string;
  tint: string;
  /** A single hue for this game, used for its rule and its bloom. */
  hue: string;
  unit: string;
  higherIsBetter: boolean;
  /** Whether this game offers a choice of difficulty. From @arena/games. */
  levels: boolean;
  how: string;
  /** Present only for solo games. Online games are played in the arena. */
  mount?: GameDef['mount'];
}

/** The first colour of a tint gradient, which is the game's identity hue. */
function hueOf(tint: string): string {
  return /#[0-9a-f]{6}/i.exec(tint)?.[0] ?? '#8b5cf6';
}

const SOLO_MODULES: GameDef[] = [
  g2048,
  aimTrainer,
  anagram,
  blackjack,
  clickSpeed,
  colorMatch,
  flappy,
  hangman,
  mathSprint,
  memory,
  minesweeper,
  pong,
  quiz,
  reactionTime,
  rockPaperScissors,
  simonSays,
  snake,
  speedTyping,
  ticTacToe,
  wordle,
];

/** The two arena modes, described here so they sit in the same grid. */
const ONLINE: Array<Omit<CatalogueEntry, 'kind' | 'unit' | 'higherIsBetter' | 'hue' | 'levels'>> = [
  {
    id: 'deathmatch',
    title: 'Arena Deathmatch',
    blurb: 'Jusqu’a quatre contre quatre. La dernière équipe debout gagne la manche.',
    category: 'Multijoueur',
    difficulty: 'Moyen',
    icon: '\u{1F52B}',
    tint: 'linear-gradient(140deg,#2563eb,#22d3ee)',
    how: 'Crée un salon, partage le code, et jouez en équipes. Au meilleur des trois manches.',
  },
  {
    id: 'crown',
    title: 'King of the Crown',
    blurb: 'Garde la couronne le plus longtemps. Buissons, pouvoirs et souterrain.',
    category: 'Multijoueur',
    difficulty: 'Difficile',
    icon: '\u{1F451}',
    tint: 'linear-gradient(140deg,#facc15,#f97316)',
    how: 'Crée un salon et partage le code. Quarante-cinq secondes de règne cumulé et c’est gagné.',
  },
];

const TINTS: Record<string, string> = {
  'two-zero-four-eight': 'linear-gradient(140deg,#8b5cf6,#c026d3)',
  'aim-trainer': 'linear-gradient(140deg,#06b6d4,#22d3ee)',
  anagram: 'linear-gradient(140deg,#ec4899,#f43f5e)',
  blackjack: 'linear-gradient(140deg,#15803d,#22c55e)',
  'click-speed': 'linear-gradient(140deg,#f59e0b,#facc15)',
  'color-match': 'linear-gradient(140deg,#8b5cf6,#22d3ee)',
  'flappy-bird': 'linear-gradient(140deg,#facc15,#a3e635)',
  hangman: 'linear-gradient(140deg,#64748b,#94a3b8)',
  'math-sprint': 'linear-gradient(140deg,#2563eb,#22d3ee)',
  memory: 'linear-gradient(140deg,#a855f7,#ec4899)',
  minesweeper: 'linear-gradient(140deg,#dc2626,#f97316)',
  pong: 'linear-gradient(140deg,#0891b2,#a3e635)',
  quiz: 'linear-gradient(140deg,#7c3aed,#2563eb)',
  'reaction-time': 'linear-gradient(140deg,#22c55e,#a3e635)',
  'rock-paper-scissors': 'linear-gradient(140deg,#f97316,#facc15)',
  'simon-says': 'linear-gradient(140deg,#ff2d95,#facc15)',
  snake: 'linear-gradient(140deg,#16a34a,#22d3ee)',
  'speed-typing': 'linear-gradient(140deg,#6366f1,#a855f7)',
  'tic-tac-toe': 'linear-gradient(140deg,#0ea5e9,#ff2d95)',
  wordle: 'linear-gradient(140deg,#15803d,#a16207)',
};

const solo: CatalogueEntry[] = SOLO_MODULES.map((g) => {
  const meta = gameById(g.id);
  if (!meta) throw new Error(`game ${g.id} is missing from @arena/games`);
  return {
    id: g.id,
    title: g.title,
    kind: 'solo',
    blurb: g.blurb,
    category: g.category,
    difficulty: g.difficulty,
    icon: g.icon,
    tint: TINTS[g.id] ?? 'linear-gradient(140deg,#8b5cf6,#ff2d95)',
    hue: hueOf(TINTS[g.id] ?? '#8b5cf6'),
    unit: meta.unit,
    higherIsBetter: meta.higherIsBetter,
    levels: meta.levels,
    how: g.how,
    mount: g.mount,
  };
});

const online: CatalogueEntry[] = ONLINE.map((g) => {
  const meta = gameById(g.id);
  if (!meta) throw new Error(`game ${g.id} is missing from @arena/games`);
  return {
    ...g,
    kind: 'online',
    unit: meta.unit,
    higherIsBetter: meta.higherIsBetter,
    levels: meta.levels,
    hue: hueOf(g.tint),
  };
});

/** Online first: they are the reason to invite somebody, and the rarer event. */
export const GAMES: CatalogueEntry[] = [
  ...online,
  ...solo.sort((a, b) => a.title.localeCompare(b.title, 'fr')),
];

export const CATEGORIES: Category[] = [
  'Multijoueur',
  'Arcade',
  'Maths',
  'Réflexion',
  'Réflexe',
  'Stratégie',
  'Culture',
  'Mots',
];

export function entryById(id: string): CatalogueEntry | null {
  return GAMES.find((g) => g.id === id) ?? null;
}

/**
 * Every game the server knows must be shown, and every game shown must be one
 * the server knows. A mismatch means a leaderboard nobody can reach, or a card
 * whose score is silently thrown away.
 */
const shown = new Set(GAMES.map((g) => g.id));
for (const meta of META) {
  if (!shown.has(meta.id)) throw new Error(`game ${meta.id} is scored but not listed`);
}
const seen = new Set<string>();
for (const g of GAMES) {
  if (seen.has(g.id)) throw new Error(`duplicate game id: ${g.id}`);
  seen.add(g.id);
}
